import * as React from "react";
import { cn } from "@/lib/cn";

/**
 * Gentelella's light badge: a tint of the colour behind the colour itself. The
 * alpha steps up in dark, where 8% over a near-black surface is not a fill.
 *
 * THE TEXT IS `-ink`, NOT THE HUE. Writing the hue on its own 8% tint measured
 * 2.54:1 for green and 2.00:1 for amber -- on the labels that say whether an
 * order was refunded or failed.
 *
 * `gray` is ringed because it is the only achromatic tone: --muted on --card is
 * 1.05:1, so a grey badge had no edge and rendered as bare text in a column
 * whose neighbours were pills -- and grey is where every unmapped status lands.
 */
const tone = {
  green: "bg-success/[0.08] text-success-ink dark:bg-success/[0.16]",
  red: "bg-destructive/[0.08] text-destructive-ink dark:bg-destructive/[0.16]",
  gray: "bg-muted text-secondary ring-1 ring-inset ring-border",
  blue: "bg-info/[0.08] text-info-ink dark:bg-info/[0.18]",
  amber: "bg-warning/[0.08] text-warning-ink dark:bg-warning/[0.16]",
};

export type BadgeTone = keyof typeof tone;

export function Badge({
  color = "gray",
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & { color?: BadgeTone }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        tone[color],
        className,
      )}
      {...props}
    />
  );
}
